import Anchor from "@/components/ui/anchor"
import { HashSection, ScrollingLinkSection } from "./schema"
import { Card, CardHeader, CardTitle } from "@/components/ui/card"
import React from "react"
import { PropertyRenderer } from "./renderer"
import { FieldRenderSubscriber } from "../subscriptions/field-render-subscriber"
import { useSectionErrors } from "./section-error-provider"
import { Trans } from "react-i18next"
import { useFormRenderer } from "./hooks"

export function AnchorHashSection({
  sectionKey,
  properties,
  infoContentKey,
  infoContentLinks,
}: HashSection) {
  const id = React.useId()
  const { registerFieldLocation } = useSectionErrors()
  return (
    <FieldRenderSubscriber
      onFieldRendered={(fieldName: string) =>
        registerFieldLocation(fieldName, sectionKey)
      }
    >
      <Anchor.HashSection sectionKey={sectionKey}>
        <CardHeader id={id}>
          <CardTitle>
            <Anchor.HashTitle />
          </CardTitle>
        </CardHeader>
        <Anchor.HashCardContent
          aria-labelledby={id}
          className="py-6"
          role="region"
        >
          <InfoContent
            infoContentKey={infoContentKey}
            infoContentLinks={infoContentLinks}
          />
          {properties.map((property, index) => (
            <PropertyRenderer formProperty={property} key={index} />
          ))}
        </Anchor.HashCardContent>
      </Anchor.HashSection>
    </FieldRenderSubscriber>
  )
}
AnchorHashSection.type = "section:hash"

export function AnchorScrollingSection({
  sectionKey,
  properties,
  infoContentKey,
  infoContentLinks,
}: ScrollingLinkSection) {
  const id = React.useId()
  const { registerFieldLocation } = useSectionErrors()
  return (
    <FieldRenderSubscriber
      onFieldRendered={(fieldName: string) =>
        registerFieldLocation(fieldName, sectionKey)
      }
    >
      <Anchor.ScrollingSection sectionKey={sectionKey}>
        <CardHeader id={id}>
          <CardTitle className="text-xl">
            <Anchor.ScrollingTitle />
          </CardTitle>
        </CardHeader>
        <Anchor.ScrollingCardContent
          aria-labelledby={id}
          className="space-y-4 py-6"
          role="group"
        >
          <InfoContent
            infoContentKey={infoContentKey}
            infoContentLinks={infoContentLinks}
          />
          {properties.map((property, index) => (
            <PropertyRenderer formProperty={property} key={index} />
          ))}
        </Anchor.ScrollingCardContent>
      </Anchor.ScrollingSection>
    </FieldRenderSubscriber>
  )
}
AnchorScrollingSection.type = "section:scrolling"

function InfoContent({
  infoContentKey,
  infoContentLinks,
}: Pick<HashSection, "infoContentKey" | "infoContentLinks">) {
  const { tFunc } = useFormRenderer()
  if (!infoContentKey) return null

  const infoContentComponents = infoContentLinks
    ? Object.fromEntries(
        Object.entries(infoContentLinks).map(([key, link]) => [
          key,
          <a
            href={link}
            key={key}
            className="hover:text-accent-foreground underline"
          />,
        ]),
      )
    : {}
  return (
    <Card className="bg-brand-light-sagecool-blue/80 border-brand-sagecool-blue border shadow-none">
      <Trans
        t={tFunc}
        i18nKey={infoContentKey}
        components={{
          ...infoContentComponents,
          b: <b />,
          strong: <strong />,
          br: <br />,
          em: <em />,
          i: <i />,
        }}
      />
    </Card>
  )
}
